import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, ScanLine, Truck } from 'lucide-react';
import { api, errorMessage, openPdf } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, ErrorState, EmptyState } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/input';
import { toast } from 'sonner';

interface Carrier { id: string; code: string; name: string; service?: string }
interface ShipmentRow {
  id: string; number: string; status: string; tracking_number: string | null; created_at: string;
  shipped_at: string | null; carrier: string; order_number: string | null; customer_name: string | null;
  qr_code: string | null; model_name: string | null;
}
interface ShipResult {
  shipment: { id: string; number: string; trackingNumber: string; service: string | null };
  trackingNumber: string;
  labelData: string;
}

function beep(ok: boolean) {
  try {
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = ok ? 1046 : 220;
    gain.gain.value = 0.06;
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.12);
    setTimeout(() => void context.close(), 400);
  } catch { /* audio no disponible */ }
}

export function LogisticsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [result, setResult] = useState<ShipResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shipping, setShipping] = useState(false);

  const carriersQuery = useQuery({ queryKey: ['carriers'], queryFn: () => api<{ data: Carrier[] }>('/carriers') });
  const shipmentsQuery = useQuery({ queryKey: ['shipments'], queryFn: () => api<{ data: ShipmentRow[] }>('/shipments'), refetchInterval: 30_000 });

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (!carrierId && carriersQuery.data?.data[0]) setCarrierId(carriersQuery.data.data[0].id);
  }, [carrierId, carriersQuery.data]);

  const ship = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim() || !carrierId) return;
    setShipping(true); setError(null); setResult(null);
    try {
      const data = await api<ShipResult>('/shipments', { method: 'POST', body: { qr: code.trim(), carrierId } });
      setResult(data);
      setCode('');
      beep(true);
      toast.success(`Expedición ${data.shipment.number} creada`);
      await qc.invalidateQueries({ queryKey: ['shipments'] });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
    } catch (err) {
      beep(false);
      setError(errorMessage(err));
    } finally {
      setShipping(false);
      inputRef.current?.focus();
    }
  };

  const deliver = async (id: string) => {
    try {
      await api(`/shipments/${id}/delivered`, { method: 'POST' });
      toast.success('Entrega confirmada');
      await qc.invalidateQueries({ queryKey: ['shipments'] });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader
        title="Logística y expediciones"
        description="Escanea el QR exterior, genera etiqueta del transportista y vincula el tracking"
        badge={<Badge tone="success"><Truck className="size-3" /> Terminal de expedición</Badge>}
      />

      <div className="grid gap-4 xl:grid-cols-[1.1fr_1fr]">
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-4 pt-5">
              <form onSubmit={ship} className="space-y-3">
                <div className="relative">
                  <ScanLine className="pointer-events-none absolute left-3 top-1/2 size-6 -translate-y-1/2 text-primary" />
                  <input
                    ref={inputRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Escanear QR exterior de la caja (ESP-…)"
                    className="scan-input pl-12 uppercase"
                    autoComplete="off"
                  />
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <Field label="Transportista" className="flex-1">
                    <Select value={carrierId} onChange={(e) => setCarrierId(e.target.value)}>
                      {(carriersQuery.data?.data ?? []).map((carrier) => <option key={carrier.id} value={carrier.id}>{carrier.name}</option>)}
                    </Select>
                  </Field>
                  <Button type="submit" className="h-9 sm:w-44" loading={shipping} disabled={!can('logistics.write') || !carrierId}>
                    <Truck /> Generar expedición
                  </Button>
                </div>
              </form>
              <p className="text-[11px] text-muted-foreground">La unidad debe estar embalada y lista para logística. El QR interno se vincula al tracking del transportista.</p>
            </CardContent>
          </Card>

          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
              <p className="font-semibold">No se puede expedir</p>
              <p className="mt-1 text-xs">{error}</p>
            </div>
          ) : null}

          {result ? (
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 size-5 text-emerald-500" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">Etiqueta generada</p>
                    <p className="mt-1 text-xs text-muted-foreground">{result.shipment.number} · {result.shipment.service ?? 'estándar'}</p>
                    <p className="mt-2 font-mono text-sm font-semibold">{result.trackingNumber}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => void openPdf(`/shipments/${result.shipment.id}/label.pdf`)}><FileText /> Imprimir etiqueta PDF</Button>
                      <Button size="sm" variant="outline" onClick={() => window.open(`/api/shipments/${result.shipment.id}/label.zpl`, '_blank', 'noopener')}>Ver ZPL</Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>

        <Card>
          <CardHeader title="Expediciones recientes" description="Tracking y estado de entrega" actions={<Button variant="ghost" size="sm" onClick={() => void shipmentsQuery.refetch()}>Actualizar</Button>} />
          {shipmentsQuery.error ? <ErrorState message={(shipmentsQuery.error as Error).message} onRetry={() => void shipmentsQuery.refetch()} /> : shipmentsQuery.data?.data.length === 0 ? (
            <EmptyState title="Sin expediciones" description="Escanea una unidad embalada para generar la primera." icon={Truck} />
          ) : (
            <div className="max-h-[600px] divide-y divide-border overflow-y-auto">
              {shipmentsQuery.data?.data.map((shipment) => (
                <div key={shipment.id} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-mono text-xs font-medium">{shipment.number}</p>
                    <StatusBadge status={shipment.status} />
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">{shipment.customer_name ?? 'Cliente'} · {shipment.order_number ?? '—'}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]">
                    <Badge tone="outline">{shipment.carrier}</Badge>
                    {shipment.tracking_number ? <span className="font-mono">{shipment.tracking_number}</span> : null}
                    <span className="text-muted-foreground">{new Date(shipment.created_at).toLocaleString('es-ES')}</span>
                  </div>
                  <div className="mt-2 flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => void openPdf(`/shipments/${shipment.id}/label.pdf`)}>Etiqueta</Button>
                    {shipment.status !== 'delivered' ? <Button variant="ghost" size="sm" onClick={() => void deliver(shipment.id)}>Marcar entregada</Button> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
